import { getStrapiBackend } from '../Contentful_API/strapiBackend';
import React from 'react';
import { createStrapiSearchClient } from './strapiSearchClient';
import { InstantSearch, SearchBox, Hits, Pagination, RefinementList, connectStateResults } from 'react-instantsearch-dom';
import { MuiThemeProvider, createMuiTheme, makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardMedia from "@material-ui/core/CardMedia";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { Link, useLocation } from 'react-router-dom';
import { getSearchDestination, getSearchTitle, getSearchTypeLabel } from './searchResultUtils';
import './search.css'; // We might need to create this or use inline styles

// searchClient will be instantiated inside the component to avoid stale cache on route changes

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    backgroundColor: '#f5f5f5',
    minHeight: '80vh',
  },
  searchBox: {
    marginBottom: theme.spacing(4),
    '& .ais-SearchBox-input': {
      width: '100%',
      padding: '12px 20px',
      fontSize: '16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
    },
    '& .ais-SearchBox-submit, & .ais-SearchBox-reset': {
      display: 'none',
    },
  },
  sidebar: {
    backgroundColor: 'white',
    padding: theme.spacing(2),
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  },
  hitCard: {
    width: '100%',
    transition: 'transform 0.2s',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    },
  },
  cardContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: '64px', // Approx 2 lines height
  },
  actions: {
    marginTop: 'auto',
    paddingTop: theme.spacing(2),
  },
  categoryTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  refinementList: {
    '& .ais-RefinementList-list': {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    '& .ais-RefinementList-item': {
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(0.5),
      borderRadius: theme.shape.borderRadius,
      transition: 'background-color 0.2s',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    '& .ais-RefinementList-label': {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      width: '100%',
      color: theme.palette.text.primary,
    },
    '& .ais-RefinementList-checkbox': {
      marginRight: theme.spacing(1.5),
      width: '18px',
      height: '18px',
      cursor: 'pointer',
    },
    '& .ais-RefinementList-labelText': {
      flexGrow: 1,
      fontSize: '0.95rem',
    },
    '& .ais-RefinementList-count': {
      marginLeft: 'auto',
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.secondary,
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 500,
    },
  },
  hitsWrapper: {
    '& .ais-Hits-list': {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: theme.spacing(3),
      listStyle: 'none',
      margin: 0,
      padding: 0,
    },
    '& .ais-Hits-item': {
      width: '100%',
    }
  },
  cardMedia: {
    height: 160,
    backgroundColor: theme.palette.grey[200],
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
}));

const Hit = ({ hit }) => {
  const classes = useStyles();
  const fields = hit.fields || hit.attributes || {};
  const title = getSearchTitle(hit);
  const link = getSearchDestination(hit, `/searchpage?q=${encodeURIComponent(title)}`);
  const typeLabel = getSearchTypeLabel(hit);
  const externalLink = /^(https?:\/\/|mailto:)/i.test(link);
  const titleLink = externalLink
    ? <a href={link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>{title}</a>
    : <Link to={link} style={{ textDecoration: 'none', color: 'inherit' }}>{title}</Link>;

  let imageUrl = null;
  if (fields) {
    // Try to find image in various potential field names (covering all Strapi aliases)
    const cover = fields?.cover || fields?.image || fields?.thumbnail || fields?.featuredImage ||
                  fields?.eventimage || fields?.event_image || fields?.EventImage ||
                  fields?.publicationimage || fields?.publicationmedia || fields?.publication_media || fields?.PublicationMedia ||
                  fields?.tenderimage || fields?.tender_image ||
                  fields?.profilepicture || fields?.profile_picture || fields?.ProfilePicture ||
                  fields?.logopartner || fields?.logo_partner || fields?.LogoPartner ||
                  fields?.logodirectorate || fields?.logo_directorate || fields?.LogoDirectorate ||
                  fields?.knowledgeimage || fields?.knowledge_image || fields?.KnowledgeImage ||
                  fields?.Thumbnail || fields?.Media || fields?.menupic || fields?.pic ||
                  fields?.Cover || fields?.media || fields?.file || fields?.Mediafile ||
                  fields?.photo || fields?.Picture || fields?.avatar || fields?.Logo ||
                  hit.cover || hit.image || hit.thumbnail || hit.publication_media || hit.event_image || hit.Media;
    
    if (cover) {
      if (Array.isArray(cover)) {
        imageUrl = cover[0]?.fields?.file?.url || cover[0]?.url || "";
      } else {
        imageUrl = cover.fields?.file?.url || cover.url || "";
      }
      
      if (!imageUrl && cover.data) {
        const data = Array.isArray(cover.data) ? cover.data[0] : cover.data;
        imageUrl = data?.attributes?.url || "";
      }

      if (!imageUrl && typeof cover === 'object') {
        // Fallback for weird data structures
        const stringVals = Object.values(cover).filter(val => typeof val === 'string' && val.startsWith('/uploads/'));
        if (stringVals.length > 0) imageUrl = stringVals[0];
      }
    }
  }

  if (imageUrl && !imageUrl.startsWith('http')) {
    const baseUrl = getStrapiBackend().uploadsUrl;
    imageUrl = `${baseUrl}${imageUrl}`;
  }

  const defaultImage = 'https://via.placeholder.com/300x160?text=No+Image';

  return (
    <Card className={classes.hitCard}>
      <CardMedia
        className={classes.cardMedia}
        image={imageUrl || defaultImage}
        title={title}
      />
      <CardContent className={classes.cardContent}>
        <Typography variant="overline" color="textSecondary">
          {typeLabel}
        </Typography>
        <Typography variant="h6" component="h3" gutterBottom className={classes.title}>
          {titleLink}
        </Typography>
        <div className={classes.actions}>
          <Button size="small" color="primary" component={externalLink ? 'a' : Link}
            {...(externalLink ? { href: link, target: '_blank', rel: 'noopener noreferrer' } : { to: link })}>
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SearchStatus = connectStateResults(({ searching, searchResults }) => (
  <Typography role="status" style={{ marginBottom: 16 }}>
    {searching ? 'Searching…' : searchResults && searchResults.nbHits === 0 ? 'No results found. Try another search.' : ''}
  </Typography>
));

const SearchList = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search).get('q') || '';
  const [searchState, setSearchState] = React.useState({ query });
  React.useEffect(() => { setSearchState({ query }); }, [query]);
  const classes = useStyles();

  const [searchError, setSearchError] = React.useState('');
  const searchClient = React.useMemo(() => createStrapiSearchClient(setSearchError), []);

  return (
    <MuiThemeProvider theme={createMuiTheme()}>
      <div className={classes.root}>
        <InstantSearch indexName="mof_public_search" searchClient={searchClient} searchState={searchState} onSearchStateChange={setSearchState}>
          <Grid container spacing={4}>
            {/* Sidebar */}
            <Grid item xs={12} md={3}>
              <div className={classes.sidebar}>
                <Typography variant="h6" className={classes.categoryTitle}>
                  Categories
                </Typography>
                <div className={classes.refinementList}>
                  <RefinementList
                    attribute="sys.contentType.sys.id"
                    showMore={true}
                    limit={10}
                    transformItems={items =>
                      items.map(item => {
                        const type = String(item.label).toLowerCase().replace(/[^a-z]/g, '');
                        return {
                          ...item,
                          label: {
                            publications: 'Publications',
                            events: 'Events',
                            menus: 'Pages',
                            submenus: 'Submenus',
                            mainmenus: 'Menus',
                            tenders: 'Tenders',
                            formerministers: 'Former Ministers',
                            directorates: 'Directorates',
                          }[type] || item.label,
                        };
                      })
                    }
                  />
                </div>
              </div>
            </Grid>

            {/* Main Content */}
            <Grid item xs={12} md={9}>
              <div className={classes.searchBox}>
                <SearchBox translations={{ placeholder: 'Search for publications, events, tenders...' }} />
              </div>

              <div role="alert">{searchError}</div>
              {!searchError && <SearchStatus />}
              <div className={classes.hitsWrapper}>
                <Hits hitComponent={Hit} />
              </div>

              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
                <Pagination />
              </div>
            </Grid>
          </Grid>
        </InstantSearch>
      </div>
    </MuiThemeProvider>
  );
};

export default SearchList;
